import { LoginProviderType } from '@common/services/auth/auth.service.interface';
import {
  PersistedStoreState,
  StoreConfig,
  basePersistenceStoreKey,
} from '@input-web/configs/store/store.config';
import { getStoreBaseActions, getStoreHydrationActions } from '@input-web/utils/store.util';
import { User as BaseUser } from '@user/models/user.model';
import { proxy, subscribe, useSnapshot } from 'valtio';

// #region Types
type User = BaseUser & { nickname?: string };
type UserState = PersistedStoreState & {
  loginType?: LoginProviderType;
  token: string;
  user?: User;
};
// #endregion

// #region State
const initial = {
  loginType: undefined,
  token: '',
  user: undefined,
} satisfies UserState;
const state = proxy<UserState>({ ...initial });
// #endregion

// #region Snapshot
export function useUserStore() {
  return useSnapshot(state);
}
// #endregion

// #region Constants
const persistenceKey = `${basePersistenceStoreKey}.user`;
// #endregion

// #region Actions
const actions = {
  ...getStoreBaseActions(state, initial),
  ...getStoreHydrationActions(persistenceKey, state),
  setLoginType: (loginType?: LoginProviderType) => {
    state.loginType = loginType;
  },
  setUser: (user?: User) => {
    state.user = user;
  },
  updateToken: (token: string) => {
    state.token = token;
  },
};
// #endregion

// #region Store Initialization
actions.hydrate();
subscribe(state, () => actions.persist());
// #endregion

// #region Store
type UserStore = StoreConfig<UserState, typeof actions>;
const userStore: UserStore = { actions, initial, state };
// #endregion

export default userStore;
